import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import { DropdownIndicator } from 'components/application/CollectionSelect';
import ModalBox from 'components/application/ModalBox';

import { resourceShape } from 'components/helpers/serialisableResources';

const provider = 'Simpro';

export default function IntegrateWithSimproModal({
  onClose,
  simproProviderConfigurations,
}) {
  const [
    simproProviderConfigurationValue,
    setSimproProviderConfigurationValue,
  ] = useState(
    formatSimproProviderConfigurationOption({
      simproProviderConfiguration: simproProviderConfigurations[0],
    }),
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'settings.externalIntegrationConfiguration.components.IntegrateWithSimproModal',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleSimproProviderConfigurationChange = (option) => {
    setSimproProviderConfigurationValue(option);
  };

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            data-method='post'
            href={`/users/auth/simpro?simpro_provider_configuration_uuid=${simproProviderConfigurationValue.value}`}
            rel='nofollow'
          >
            {i18nTCommon('integrations.integrateWith', { provider })}
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-p-8'>
        <h2 className='tw-mb-4 tw-mt-0 tw-text-l tw-font-semibold tw-text-grey-900'>
          {i18nT('body.title')}
        </h2>
        <SimproProviderConfigurationSelector
          onChange={handleSimproProviderConfigurationChange}
          simproProviderConfigurations={simproProviderConfigurations}
          value={simproProviderConfigurationValue}
        />
      </div>
    </ModalBox>
  );
}

IntegrateWithSimproModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  simproProviderConfigurations: PropTypes.arrayOf(
    resourceShape('simproProviderConfiguration'),
  ).isRequired,
};

function SimproProviderConfigurationSelector({
  onChange,
  simproProviderConfigurations,
  value,
}) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const simproProviderConfigurationOptions = simproProviderConfigurations.map(
    (simproProviderConfiguration) =>
      formatSimproProviderConfigurationOption({ simproProviderConfiguration }),
  );

  return (
    <div>
      <label
        className='collection-select__label tw-font-medium'
        htmlFor='simproProviderConfiguration'
      >
        Simpro account
      </label>
      <Select
        autoFocus={false}
        className='collection-select__select-container'
        classNamePrefix='collection-select'
        components={{ Option, DropdownIndicator }}
        controlShouldRenderValue={true}
        id='simproProviderConfiguration'
        isSearchable={true}
        loadingMessage={() => null}
        name='simproProviderConfiguration'
        noOptionsMessage={() => (
          <div className='tw-text-grey-500'>
            {i18nTShared('noMatchingResults')}
          </div>
        )}
        onChange={onChange}
        options={simproProviderConfigurationOptions}
        styles={{
          input: (provided, _state) => ({
            ...provided,
            margin: 0,
            paddingBottom: 0,
            paddingTop: 0,
          }),
        }}
        value={value}
      />
    </div>
  );
}

export function Option(props) {
  return (
    <components.Option {...props}>
      <div className='collection-select__option_container'>
        <span>{props.children}</span>
      </div>
    </components.Option>
  );
}

const formatSimproProviderConfigurationOption = ({
  simproProviderConfiguration,
}) =>
  simproProviderConfiguration ?
    {
      value: simproProviderConfiguration.attributes.uuid,
      label: simproProviderConfiguration.attributes.build,
    }
  : null;
